// A routing rule, as the sentence it reads as (owner, 2026-09-15; a
// required-approval set since 2026-10-09 — D-336: "Rule sentences should be
// structured as "When... , require the following approvals:"").
//
// ONE BUILDER, TWO CONSUMERS — the Rule column on Rules -> Routing and the
// "Route decided" row in an invoice's History (D-343) — so the two cannot describe
// one rule differently. The same rule Rules -> Codes already follows for its
// own sentence.
//
// THE SCOPE CLAUSE IS ELIDED WHEN IT REPEATS THE ONE ABOVE IT. That is what
// makes the sentence read naturally:
//
//   When the invoice's vendor is XEROX BUSINESS SOLUTIONS and total is at least
//   $1,000.00, require the following approvals:
//
// rather than repeating "the invoice's" before every clause. The dropdown still
// renders on every row in the editor, so each condition stays editable; only the
// rendered sentence elides.
//
// Eliding is safe because evaluation groups line-scoped conditions: consecutive
// `any line item's` clauses are satisfied by ONE line, which is exactly what the
// elided sentence says.

import { APPROVAL_REQUIREMENT_LABELS, APPROVAL_REQUIREMENTS, REQUIRED_APPROVAL_FIELD } from '@ap/shared'
import type { OversightRule, OversightRuleCondition, RequiredApprovals } from '../api/types'
import { formatCurrency, formatNumericDate } from './format'
import {
  OVERSIGHT_FIELD_LABEL,
  OVERSIGHT_FIELD_TYPE,
  OVERSIGHT_OPERATOR_CLAUSE,
  OVERSIGHT_SCOPE_CLAUSE,
} from './oversightRules'

/**
 * The value clause.
 *
 * An entity's values join with ` or ` — that is where the `or` in "company is
 * SeniorLIFE Lehigh Valley or SeniorLIFE Reading" comes from, and why the screen
 * needs no connector control. Each value prints its resolved label, falling back
 * to the stored id, so a rule naming a retired vendor still reads as a
 * sentence rather than a blank.
 */
function valueClause(condition: OversightRuleCondition): string {
  switch (OVERSIGHT_FIELD_TYPE[condition.field]) {
    case 'entity': {
      const ids = condition.value_ids ?? []
      const labels = condition.value_labels ?? []
      return ids.map((id, index) => labels[index] ?? id).join(' or ')
    }
    case 'text':
      return `"${condition.value_text ?? ''}"`
    case 'money':
      return formatCurrency(condition.value_number)
    case 'number': {
      // A quantity, stored at four decimal places. Trailing zeroes are noise in
      // a sentence: "quantity is at least 2.5", never "2.5000".
      const raw = (condition.value_number ?? '').trim()
      return raw.includes('.') ? raw.replace(/0+$/, '').replace(/\.$/, '') : raw
    }
    default:
      return formatNumericDate(condition.value_date)
  }
}

/** One condition, with its scope clause included or elided. */
function conditionClause(condition: OversightRuleCondition, includeScope: boolean): string {
  const scope = includeScope ? `${OVERSIGHT_SCOPE_CLAUSE[condition.scope]} ` : ''
  const field = OVERSIGHT_FIELD_LABEL[condition.field]
  const operator = OVERSIGHT_OPERATOR_CLAUSE[condition.operator]
  return `${scope}${field} ${operator} ${valueClause(condition)}`
}

/**
 * The closing clause (owner, 2026-10-09 — D-336): "Rule sentences should be
 * structured as "When... , require the following approvals:"". The table's
 * four columns, and the dialog's four toggles, say which.
 */
export const ROUTING_RULE_CLOSING = 'require the following approvals:'

/** "When <every condition>", the scope clause elided where it repeats. Null for
    a rule with no conditions. */
function whenClause(rule: Pick<OversightRule, 'conditions'>): string | null {
  const conditions = [...rule.conditions].sort((a, b) => a.position - b.position)
  if (conditions.length === 0) return null
  const clauses = conditions.map((condition, index) =>
    conditionClause(condition, index === 0 || condition.scope !== conditions[index - 1]?.scope),
  )
  return `When ${clauses.join(' and ')}`
}

/**
 * The whole rule as the Rule column reads it: "When ..., require the following
 * approvals:". A rule with no conditions cannot be written through the API and
 * matches nothing if one exists anyway, so it renders as the plain statement of
 * that rather than as a broken sentence.
 */
export function renderOversightRuleSentence(rule: Pick<OversightRule, 'conditions'>): string {
  const when = whenClause(rule)
  return when === null ? 'This rule names no criteria and matches nothing.' : `${when}, ${ROUTING_RULE_CLOSING}`
}

/** The required steps by name, in pipeline order, joined with "and" and the
    Oxford comma: "Controller Approval and Director Approval". Empty for none. */
export function requiredApprovalNames(flags: RequiredApprovals): string[] {
  return APPROVAL_REQUIREMENTS.filter((key) => flags[REQUIRED_APPROVAL_FIELD[key]]).map(
    (key) => APPROVAL_REQUIREMENT_LABELS[key],
  )
}

/** "A", "A and B", "A, B, and C" — the Oxford comma. */
export function andList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/**
 * The rule as ONE COMPLETE SENTENCE with its approvals named, for History's
 * routing rows (D-336): "When the invoice's total is at least $1,000.00,
 * require the following approvals: Controller Approval and Director
 * Approval." A rule with every step off reads "..., require no approvals."
 */
export function routingRuleSentenceWithApprovals(rule: Pick<OversightRule, 'conditions'> & RequiredApprovals): string {
  const when = whenClause(rule)
  const names = requiredApprovalNames(rule)
  if (when === null) return 'This rule names no criteria and matches nothing.'
  return names.length === 0 ? `${when}, require no approvals.` : `${when}, ${ROUTING_RULE_CLOSING} ${andList(names)}.`
}

/**
 * WHEN SEVERAL RULES MATCH AT ONCE (owner, 2026-10-09 — D-343): the route is
 * every step any of them requires, with Controller Approval standing in for
 * Manager Approval, since an invoice takes one or the other — the same merge
 * `resolveRequiredApprovals` in `backend/src/domain/routing.ts` makes. The
 * owner's sentence: "When multiple rules apply, require all applicable
 * approvals with priority given to Controllers over Managers: Controller
 * Approval and Director Approval."
 */
export function combinedRoutingSentence(rules: readonly RequiredApprovals[]): string {
  const names = combinedApprovalNames(rules)
  const lead = 'When multiple rules apply, require all applicable approvals with priority given to Controllers over Managers'
  return names.length === 0 ? `${lead}: none.` : `${lead}: ${andList(names)}.`
}

/** The steps several matching rules require together, by name, in pipeline
    order: every step any of them requires, Controller standing in for Manager. */
function combinedApprovalNames(rules: readonly RequiredApprovals[]): string[] {
  const any = (key: keyof RequiredApprovals) => rules.some((rule) => rule[key])
  return requiredApprovalNames({
    requires_controller: any('requires_controller'),
    requires_manager: any('requires_manager') && !any('requires_controller'),
    requires_director: any('requires_director'),
    requires_oversight: any('requires_oversight'),
  })
}
